import { toast } from 'react-toastify';
import type { FieldErrors } from 'react-hook-form';

import { defaultToastOptions } from '@/toast';

import { buildFieldErrorToast, selectInvisibleMessages } from './field-error-summary';
import { collectVisibleFieldMessages, focusFirstInvalidField } from './focus-first-invalid-field';

/**
 * Numara mesajele de eroare dintr-un arbore `formState.errors` (react-hook-form), pentru cazul in
 * care o eroare de Zod cade pe o cale care nu are niciun input randat (SE-07: `.refine()` pe un
 * camp obiect, nu pe frunza) — `focusFirstInvalidField()` gaseste atunci 0 campuri in DOM, desi
 * exista o eroare reala. Se opreste la primul `message` gasit pe fiecare ramura, ca sa nu coboare
 * in `ref` (element DOM) sau `type`, care sunt si ele obiecte.
 */
export function numaraEroriDeFormular(erori: FieldErrors | undefined): number {
    if (!erori) return 0;

    let numar = 0;
    const viziteaza = (nod: unknown): void => {
        if (!nod || typeof nod !== 'object') return;
        const mesaj = (nod as { message?: unknown }).message;
        if (typeof mesaj === 'string' && mesaj) {
            numar += 1;
            return;
        }
        for (const valoare of Object.values(nod as Record<string, unknown>)) {
            if (valoare && typeof valoare === 'object') viziteaza(valoare);
        }
    };
    viziteaza(erori);
    return numar;
}

/**
 * Ce se intampla cand un submit e respins de validare — indiferent CINE l-a respins.
 *
 * 🔴 De ce exista: pana acum, erorile de la BACKEND (400 cu `errors.<camp>`) duceau utilizatorul
 * la primul camp invalid, iar cele de la Zod, pe CLIENT, nu faceau nimic — masurat: `scrollY=0`,
 * `activeElement=BODY`, cu noua campuri marcate undeva mai sus in pagina. Pentru utilizator cele
 * doua situatii arata identic (a apasat „Salveaza" si n-a mers), deci trebuie sa se si poarte
 * identic. Pe unele formulare nu se intampla nici macar un toast: `form.handleSubmit(onValid)`
 * fara al doilea argument inseamna ca la validare esuata **nu se executa nimic**, dialogul se
 * inchide, si utilizatorul ramane fara niciun semnal.
 *
 * Cele doua tipare de submit din proiect se conecteaza amandoua aici:
 * ```ts
 * // A: validare manuala
 * const isValid = await form.trigger();
 * if (isValid) dialog.show(); else handleInvalidSubmit();
 *
 * // B: react-hook-form
 * form.handleSubmit(onValid, handleInvalidSubmit)
 * ```
 *
 * @param fieldErrors erorile pe campuri venite de la backend, daca submitul a fost respins de
 *   server. Mesajele care nu se vad nicaieri in pagina ajung in toast — vezi
 *   `buildFieldErrorToast`. Pentru o respingere pe client se omite.
 * @param mesajDeRezerva ce se afiseaza cand nu e marcat niciun camp si nici backendul n-a trimis
 *   ceva utilizabil.
 * @param obtineEroriFormular SE-07: citeste `form.formState.errors` la nevoie, ca fallback cand
 *   DOM-ul are 0 campuri marcate (eroare pe o cale fara input randat). Se cere ca FUNCTIE, nu ca
 *   valoare — trebuie citita LAZY, din interiorul `requestAnimationFrame`-ului de mai jos: imediat
 *   dupa `form.trigger()`, `formState.errors` e inca instantaneul randarii anterioare (acelasi
 *   motiv pentru care `focusFirstInvalidField` numara din DOM, nu din `formState` — vezi acolo).
 */
export function handleInvalidSubmit(
    fieldErrors?: Record<string, string>,
    mesajDeRezerva = 'Nu s-a putut trimite. Verifică datele introduse.',
    obtineEroriFormular?: () => FieldErrors | undefined
): void {
    // Asteptam randarea lui `aria-invalid` si a mesajelor inainte de a citi DOM-ul.
    requestAnimationFrame(() => {
        const marcate = focusFirstInvalidField();
        const invizibile = selectInvisibleMessages(fieldErrors, collectVisibleFieldMessages());
        let rezultat = buildFieldErrorToast(marcate, invizibile);

        // Fallback SE-07: 0 marcate in DOM nu inseamna 0 erori — poate insemna o eroare pe o cale
        // fara input randat. Numai cand chipurile nu vin deja de la backend (acolo `marcate`/
        // `invizibile` acopera deja cazul) si numai cand tot n-avem ce arata.
        if (!rezultat && marcate === 0 && !fieldErrors) {
            const numarFormular = numaraEroriDeFormular(obtineEroriFormular?.());
            if (numarFormular > 0) rezultat = buildFieldErrorToast(numarFormular, invizibile);
        }

        // `autoClose: false` cand utilizatorul nu poate corecta singur: mesajul e dovada pe care
        // o duce mai departe, nu o instructiune de urmat. Un text care dispare singur o ia cu el.
        toast.error(rezultat?.mesaj ?? mesajDeRezerva, {
            ...defaultToastOptions,
            ...(rezultat?.persistent ? { autoClose: false as const } : {}),
        });
    });
}
